import { Link, useParams } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { MetricCard } from '../components/MetricCard';
import { Notice } from '../components/Notice';
import { moduleTrainingScore } from '../training';

const labels = {
  'read-switch': ['動きの識別', '動き'],
  'central-peripheral': ['中央・周辺の同時判断', '同時判断'],
  'player-track': ['複数対象の追跡', '追跡'],
  'moving-target': ['動く記号', '識別'],
} as const;

export function ResultsPage() {
  const { sessionId = '' } = useParams();
  const app = useApp();
  const session = app.sessions.find((item) => item.id === sessionId)
    ?? (app.lastCompletion?.session.id === sessionId ? app.lastCompletion.session : undefined);
  const summaries = app.summariesBySession[sessionId]
    ?? (app.lastCompletion?.session.id === sessionId ? app.lastCompletion.summaries : []);

  if (!session) {
    return <div className="page"><div className="empty-state"><div><strong>結果が見つかりません。</strong><Link className="button" to="/history">履歴へ</Link></div></div></div>;
  }
  if (session.mode === 'familiarization') {
    return <div className="page"><div className="empty-state"><div><strong>採点なしのお試しは完了です。</strong><p>操作できたことだけを、このブラウザ内に記録しました。</p><Link className="button" to="/">ホームへ</Link></div></div></div>;
  }
  const scores = summaries.map((summary) => moduleTrainingScore(summary)).filter((value): value is number => value !== undefined);
  const overallScore = scores.length ? scores.reduce((total, score) => total + score, 0) / scores.length : undefined;
  const repeatHref = session.moduleIds.length === 1 ? `/session?module=${session.moduleIds[0]}` : '/session';

  return (
    <div className="page">
      <header className="page-heading"><div><div className="eyebrow">今回の結果</div><h1>{overallScore === undefined ? '練習記録を保存しました' : `今回のスコア ${Math.round(overallScore)} / 100`}</h1><p>正答率と到達難易度を確認できます。無効になった試行は集計から除いています。</p></div><span className="tag good">保存しました</span></header>
      <section className="grid-4">
        {summaries.map((summary) => {
          const [label, badge] = labels[summary.moduleId];
          const threshold = summary.threshold;
          const hasThreshold = threshold !== undefined;
          const value = threshold !== undefined
            ? threshold.toFixed(summary.thresholdUnit === 'ms' ? 0 : summary.moduleId === 'moving-target' ? 1 : 2)
            : `${Math.round(summary.accuracy * 100)}`;
          const unit = hasThreshold
            ? summary.thresholdUnit === 'ms' ? 'ms'
              : summary.thresholdUnit === 'arcmin' ? '分角'
                : summary.thresholdUnit === 'percent' ? '%'
                  : '度/秒'
            : '%';
          const metricLabel = hasThreshold
            ? summary.moduleId === 'read-switch' ? '2回の動きを見分けられた表示時間'
              : summary.moduleId === 'central-peripheral' ? '中央と周辺を読めた表示時間'
                : summary.moduleId === 'player-track' ? '4個を追い続けられた速さ'
                  : '見分けられた最小サイズ'
            : summary.moduleId === 'player-track' ? '正しく選べた割合' : summary.moduleId === 'moving-target' ? '正しく見分けた割合' : label;
          const detail = hasThreshold
            ? `${summary.thresholdUnit === 'ms' ? '短いほど難しい条件' : summary.moduleId === 'player-track' ? '追えた速さ' : '速度と連動して見分けた大きさ'}・${summary.validTrials}回を集計`
            : summary.medianReactionTimeMs
              ? `正解したときの反応時間：${Math.round(summary.medianReactionTimeMs)}ミリ秒`
              : `同じ難しさで${summary.validTrials}回を集計`;
          const score = moduleTrainingScore(summary);
          return <MetricCard key={summary.moduleId} label={metricLabel} badge={badge} value={value} unit={unit} detail={detail} change={score === undefined ? undefined : `課題スコア：${Math.round(score)} / 100`} changeTone="positive" />;
        })}
      </section>
      <div className="section-row"><h2>詳しい記録</h2><span className="status-label">数値のばらつきも表示</span></div>
      <section className="panel result-detail-wrap">
        <table className="history-table result-detail-table">
          <thead><tr><th>課題</th><th>正しく答えた割合</th><th>正解時の反応時間</th><th>答えなかった回数</th><th>誤って反応した回数</th><th>集計した回数</th></tr></thead>
          <tbody>{summaries.map((summary) => {
            const [label] = labels[summary.moduleId];
            const ci = summary.accuracyCi95
              ? ` [${Math.round(summary.accuracyCi95[0] * 100)}–${Math.round(summary.accuracyCi95[1] * 100)}%]`
              : ' / CI —';
            const rt = summary.medianReactionTimeMs === undefined ? '—' : `${Math.round(summary.medianReactionTimeMs)} ms`;
            const iqr = summary.reactionTimeIqrMs ? ` [${Math.round(summary.reactionTimeIqrMs[0])}–${Math.round(summary.reactionTimeIqrMs[1])}]` : '';
            const conditions = summary.conditionSummaries?.map((condition) =>
              `${condition.label} ${Math.round(condition.accuracy * 100)}% (${condition.validTrials}回)`,
            ).join(' / ');
            return <tr key={summary.moduleId}><td><strong>{label}</strong>{conditions && <small>{conditions}</small>}</td><td>{Math.round(summary.accuracy * 100)}%<small>{ci}</small></td><td>{rt}{iqr}</td><td>{summary.omissions}</td><td>{summary.falseResponses}</td><td>{summary.validTrials}</td></tr>;
          })}</tbody>
        </table>
      </section>
      <div className="section-row"><h2>測定品質</h2></div>
      <section className="panel result-quality">
        <div><span className="panel-label">種類</span><strong>{session.moduleIds.length === 1 ? '1課題練習' : '4課題練習'}</strong></div>
        <div><span className="panel-label">集計した回数</span><strong>{summaries.reduce((sum, item) => sum + item.validTrials, 0)}</strong></div>
        <div><span className="panel-label">疲労感</span><strong>{session.fatigue} / 5</strong></div>
        <div><span className="panel-label">データ形式</span><strong>{session.protocolVersion}</strong></div>
      </section>
      <Notice title="1回の上下だけで結論を出さないでください。" tone="warning">同じ環境で練習を繰り返し、表示期間を変えながら時系列の傾向を確認してください。</Notice>
      <div className="result-actions"><Link className="button" to={repeatHref}>同じ内容をもう一度</Link><Link className="button secondary" to="/history">スコアの推移を見る</Link></div>
    </div>
  );
}
